// shared/utils/policyComplianceCalculator.js - 통합 Policy Compliance 계산 함수
/**
 * 모든 플랫폼에서 사용할 통합 Policy Compliance 계산 함수
 * 단일 진실의 근원(Single Source of Truth) 역할
 */

// Firebase imports will be passed as parameters to avoid path issues
// import { db } from '../../firebaseConfig';
// import { collection, getDocs, query, where } from 'firebase/firestore';

/**
 * 통합 Policy Compliance 계산 함수
 * @param {string} userId - 사용자 ID
 * @param {string} appId - 앱 ID (기본값: 'kairos-platform-1ffde')
 * @param {Object} firebaseUtils - Firebase 유틸리티 객체 { db, collection, getDocs, query, where }
 * @param {Object} options - 추가 옵션
 * @returns {Promise<Object>} Policy Compliance 결과
 */
export const calculatePolicyCompliance = async (userId, appId = 'kairos-platform-1ffde', firebaseUtils, options = {}) => {
  try {
    // 기본 설정
    const requiredMonthlyAmount = options.requiredMonthlyAmount || 300; // USD
    const currentDate = new Date();
    
    // 1. 사용자의 고용현황 조회
    const { db, collection, getDocs } = firebaseUtils;
    const employmentsRef = collection(db, 'artifacts', appId, 'users', userId, 'employments');
    const employmentsSnapshot = await getDocs(employmentsRef);
    
    if (employmentsSnapshot.empty) {
      return {
        status: 'no_employment_data',
        compliance: 0,
        companyName: '',
        monthlyAverage: 0,
        totalRemittance: 0,
        monthsEmployed: 0,
        message: 'No employment data available'
      };
    }

    const employments = employmentsSnapshot.docs.map(doc => ({ 
      id: doc.id, 
      ...doc.data() 
    }));
    
    // 2. 현재 활성 고용계약 찾기 (고용중 상태만)
    const activeEmployment = employments.find(emp => {
      const startDate = new Date(emp.contractStartDate);
      const endDate = new Date(emp.contractEndDate);
      return emp.isActive && 
             emp.employmentStatus === 'employed' && 
             startDate <= currentDate && 
             endDate >= currentDate;
    });

    // 3. 활성 고용이 없는 경우 처리
    if (!activeEmployment) {
      // 가장 최근 고용 데이터 기준으로 상태 반환
      const latestEmployment = employments.sort((a, b) => 
        new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
      )[0];
      
      if (latestEmployment) {
        const status = latestEmployment.employmentStatus || 'no_employment_data';
        const statusMessages = {
          'returned_myanmar': 'Returned to Myanmar',
          'terminated': 'Employment terminated',
          'job_searching': 'Currently job searching',
          'contract_extension': 'Contract extension in progress',
          'no_employment_data': 'No employment data available'
        };
        
        return {
          status,
          compliance: 0,
          companyName: latestEmployment.companyName || '',
          monthlyAverage: 0,
          totalRemittance: 0,
          monthsEmployed: 0,
          message: statusMessages[status] || 'Not currently employed'
        };
      }
      
      return {
        status: 'no_employment_data',
        compliance: 0,
        companyName: '',
        monthlyAverage: 0,
        totalRemittance: 0,
        monthsEmployed: 0,
        message: 'No employment data available'
      };
    }

    // 4. 고용 시작일부터 현재까지의 송금 내역 (중앙 + 사용자 하위 컬렉션 병합)
    const startDate = new Date(activeEmployment.contractStartDate);
    const { query: fsQuery, where: fsWhere } = firebaseUtils;

    console.log(`🔍 Policy Compliance 디버깅 - 사용자 ${userId}:`, {
      contractStartDate: activeEmployment.contractStartDate,
      startDate: startDate.toISOString(),
      companyName: activeEmployment.companyName
    });

    const byKey = new Map();
    try {
      const userSubRef = collection(db, 'artifacts', appId, 'users', userId, 'remittances');
      const userSnap = await getDocs(userSubRef);
      userSnap.docs.forEach((docSnap) => {
        const data = docSnap.data();
        const key = data.remitId || docSnap.id;
        byKey.set(key, { id: docSnap.id, ...data });
      });
    } catch (subErr) {
      console.warn(`⚠️ users/${userId}/remittances 조회 실패:`, subErr?.message || subErr);
    }

    try {
      const centralRef = collection(db, 'artifacts', appId, 'remittances');
      const centralQ = fsQuery
        ? fsQuery(centralRef, fsWhere('userId', '==', userId))
        : centralRef;
      const centralSnap = await getDocs(centralQ);
      centralSnap.docs.forEach((docSnap) => {
        const data = docSnap.data();
        const key = data.remitId || docSnap.id;
        if (!byKey.has(key)) {
          byKey.set(key, { id: docSnap.id, ...data });
        }
      });
    } catch (centralErr) {
      console.warn(`⚠️ 중앙 remittances 조회 실패:`, centralErr?.message || centralErr);
    }

    const allRemittances = Array.from(byKey.values());
    
    console.log(`📊 전체 송금 데이터 (${allRemittances.length}개):`, allRemittances.map(r => ({
      id: r.id,
      status: r.status,
      amountUSD: r.amountUSD,
      actualAmountUSD: r.actualAmountUSD,
      amount: r.amount,
      createdAt: r.createdAt
    })));
    
    // 고용 시작일 이후의 완료된 송금만 필터링
    const remittances = allRemittances.filter(remit => {
      const remitDate = remit.createdAt?.toDate ? remit.createdAt.toDate() : new Date(remit.createdAt);
      const isAfterStart = remitDate >= startDate;
      const isCompleted = remit.status === 'COMPLETED';
      
      console.log(`🔍 송금 필터링:`, {
        remitId: remit.id,
        remitDate: remitDate.toISOString(),
        startDate: startDate.toISOString(),
        isAfterStart,
        isCompleted,
        status: remit.status,
        amountUSD: remit.amountUSD,
        actualAmountUSD: remit.actualAmountUSD,
        amount: remit.amount,
        finalAmount: remit.amountUSD || remit.actualAmountUSD || remit.amount || 0
      });
      
      return isAfterStart && isCompleted;
    });
    
    console.log(`✅ 필터링된 송금 데이터 (${remittances.length}개):`, remittances.map(r => ({
      id: r.id,
      amountUSD: r.amountUSD,
      actualAmountUSD: r.actualAmountUSD,
      amount: r.amount
    })));
    
    // 5. 고용 개월수 계산 (동적 기간 계산)
    const nextMonth = new Date(startDate);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    nextMonth.setDate(1); // 다음달 1일
    
    // 🔥 중요: 금월에 송금이 있는지 확인
    const currentMonth = new Date(currentDate);
    currentMonth.setDate(1); // 현재월 1일
    
    // 🔥 중요: allRemittances에서 금월 송금 확인 (COMPLETED 상태만)
    const hasCurrentMonthRemittance = allRemittances.some(remit => {
      const remitDate = remit.createdAt?.toDate ? remit.createdAt.toDate() : new Date(remit.createdAt);
      const isAfterStart = remitDate >= startDate;
      const isCompleted = remit.status === 'COMPLETED';
      const remitMonth = new Date(remitDate);
      remitMonth.setDate(1);
      const isCurrentMonth = remitMonth.getTime() === currentMonth.getTime();
      
      return isAfterStart && isCompleted && isCurrentMonth;
    });
    
    // 🔥 중요: 항상 현재월까지로 설정 (동적 기간 계산 문제 해결)
    const endMonth = currentMonth;
    
    // 정확한 개월수 계산 (년/월 기준)
    const monthsEmployed = Math.max(1, 
      (endMonth.getFullYear() - nextMonth.getFullYear()) * 12 + 
      (endMonth.getMonth() - nextMonth.getMonth()) + 1
    );
    
    console.log(`📅 동적 기간 계산:`, {
      contractStartDate: startDate.toISOString(),
      nextMonth: nextMonth.toISOString(),
      currentMonth: currentMonth.toISOString(),
      hasCurrentMonthRemittance,
      endMonth: endMonth.toISOString(),
      monthsEmployed
    });
    
    // 6. 동적 기간에 맞는 송금 데이터 재필터링
    const finalRemittances = allRemittances.filter(remit => {
      const remitDate = remit.createdAt?.toDate ? remit.createdAt.toDate() : new Date(remit.createdAt);
      const isAfterStart = remitDate >= startDate;
      const isCompleted = remit.status === 'COMPLETED';
      const remitMonth = new Date(remitDate);
      remitMonth.setDate(1);
        // 🔥 중요: 시간대 차이로 인한 오류 방지 - 월 단위로만 비교
        const isWithinPeriod = remitMonth.getTime() >= nextMonth.getTime() && 
                             remitMonth.getTime() <= endMonth.getTime();
      
      return isAfterStart && isCompleted && isWithinPeriod;
    });
    
    // 7. 총 송금액 계산 (USD 기준) - 동적 기간에 맞는 송금 데이터 사용
    const totalRemittance = finalRemittances.reduce((sum, remit) => 
      sum + (remit.amountUSD || remit.actualAmountUSD || remit.amount || 0), 0);
    
    console.log(`💰 총 송금액 계산:`, {
      originalRemittancesCount: remittances.length,
      finalRemittancesCount: finalRemittances.length,
      totalRemittance,
      requiredMonthlyAmount,
      finalRemittancesDetails: finalRemittances.map(r => ({
        id: r.id,
        amountUSD: r.amountUSD,
        actualAmountUSD: r.actualAmountUSD,
        amount: r.amount,
        status: r.status
      }))
    });
    
    // 🔥 중요: 송금액이 0인 경우 Policy Compliance도 0이어야 함
    if (totalRemittance === 0) {
      console.log(`⚠️ 송금액이 0인 경우 Policy Compliance 0% 반환:`, {
        userId,
        totalRemittance,
        monthsEmployed,
        companyName: activeEmployment.companyName
      });
      
      return {
        status: 'employed',
        compliance: 0,
        monthlyAverage: 0,
        totalRemittance: 0,
        monthsEmployed,
        companyName: activeEmployment.companyName || '',
        contractStartDate: activeEmployment.contractStartDate,
        contractEndDate: activeEmployment.contractEndDate,
        message: 'No remittances found - Compliance: 0%'
      };
    }
    
    // 7. 월 평균 송금액 계산
    const monthlyAverage = totalRemittance / monthsEmployed;
    
    // 8. Policy Compliance 계산 (월 평균 기준)
    const compliance = Math.min(100, (monthlyAverage / requiredMonthlyAmount) * 100);
    
    console.log(`📊 Policy Compliance 최종 계산:`, {
      userId,
      totalRemittance,
      monthsEmployed,
      monthlyAverage,
      requiredMonthlyAmount,
      compliance,
      companyName: activeEmployment.companyName,
      calculation: `${monthlyAverage} / ${requiredMonthlyAmount} * 100 = ${compliance}%`
    });
    
    // 9. 결과 반환
    return {
      status: 'employed',
      compliance: Math.round(compliance),
      monthlyAverage: Math.round(monthlyAverage),
      totalRemittance: Math.round(totalRemittance),
      monthsEmployed,
      companyName: activeEmployment.companyName || '',
      contractStartDate: activeEmployment.contractStartDate,
      contractEndDate: activeEmployment.contractEndDate,
      message: `Compliance: ${Math.round(compliance)}% (${Math.round(monthlyAverage)} USD/month)`
    };
    
  } catch (error) {
    console.error('Policy Compliance 계산 오류:', error);
    return {
      status: 'error',
      compliance: 0,
      companyName: '',
      monthlyAverage: 0,
      totalRemittance: 0,
      monthsEmployed: 0,
      message: 'Calculation error occurred'
    };
  }
};

/**
 * Cloud Functions용 Policy Compliance 계산 함수 (Firebase Admin SDK 사용)
 * @param {string} userId - 사용자 ID
 * @param {string} appId - 앱 ID
 * @param {Object} adminDb - Firebase Admin SDK의 db 객체
 * @param {Object} options - 추가 옵션
 * @returns {Promise<Object>} Policy Compliance 결과
 */
export const calculatePolicyComplianceForCloudFunction = async (userId, appId = 'kairos-platform-1ffde', adminDb, options = {}) => {
  try {
    // 기본 설정
    const requiredMonthlyAmount = options.requiredMonthlyAmount || 300; // USD
    const currentDate = new Date();
    
    // 1. 사용자의 고용현황 조회 (Firebase Admin SDK)
    const employmentsRef = adminDb
      .collection(`artifacts/${appId}/users`)
      .doc(userId)
      .collection('employments');
    const employmentsSnapshot = await employmentsRef.get();
    
    if (employmentsSnapshot.empty) {
      return {
        status: 'no_employment_data',
        compliance: 0,
        companyName: '',
        monthlyAverage: 0,
        totalRemittance: 0,
        monthsEmployed: 0,
        message: 'No employment data available'
      };
    }

    const employments = employmentsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // 2. 현재 활성 고용계약 찾기
    const activeEmployment = employments.find(emp => {
      const startDate = new Date(emp.contractStartDate);
      const endDate = new Date(emp.contractEndDate);
      return emp.isActive && 
             emp.employmentStatus === 'employed' && 
             startDate <= currentDate && 
             endDate >= currentDate;
    });

    // 3. 활성 고용이 없는 경우 처리
    if (!activeEmployment) {
      const latestEmployment = employments.sort((a, b) => 
        new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
      )[0];
      
      if (latestEmployment) {
        const status = latestEmployment.employmentStatus || 'no_employment_data';
        const statusMessages = {
          'returned_myanmar': 'Returned to Myanmar',
          'terminated': 'Employment terminated',
          'job_searching': 'Currently job searching',
          'contract_extension': 'Contract extension in progress',
          'no_employment_data': 'No employment data available'
        };
        
        return {
          status,
          compliance: 0,
          companyName: latestEmployment.companyName || '',
          monthlyAverage: 0,
          totalRemittance: 0,
          monthsEmployed: 0,
          message: statusMessages[status] || 'Not currently employed'
        };
      }
      
      return {
        status: 'no_employment_data',
        compliance: 0,
        companyName: '',
        monthlyAverage: 0,
        totalRemittance: 0,
        monthsEmployed: 0,
        message: 'No employment data available'
      };
    }

    // 4. 고용 시작일부터 현재까지의 송금 내역 조회 (remittances 컬렉션 사용)
    const startDate = new Date(activeEmployment.contractStartDate);
    const remittancesRef = adminDb
      .collection(`artifacts/${appId}/users`)
      .doc(userId)
      .collection('remittances');
    
    // Firestore 쿼리 사용 (Cloud Functions에서는 권한 문제 없음)
    const remittancesQuery = remittancesRef
      .where('createdAt', '>=', startDate)
      .where('status', '==', 'COMPLETED');
    
    const remittancesSnapshot = await remittancesQuery.get();
    const remittances = remittancesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // 5. 총 송금액 계산 (USD 기준)
    const totalRemittance = remittances.reduce((sum, remit) => 
      sum + (remit.amountUSD || remit.actualAmountUSD || remit.amount || 0), 0);
    
    // 6. 고용 개월수 계산 (계약일 다음달부터 현재기준 전월까지)
    const nextMonth = new Date(startDate);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    nextMonth.setDate(1); // 다음달 1일
    
    const lastMonth = new Date(currentDate);
    lastMonth.setMonth(lastMonth.getMonth() - 1);
    lastMonth.setDate(1); // 전월 1일
    
    // 정확한 개월수 계산 (년/월 기준)
    const monthsEmployed = Math.max(1, 
      (lastMonth.getFullYear() - nextMonth.getFullYear()) * 12 + 
      (lastMonth.getMonth() - nextMonth.getMonth()) + 1
    );
    
    // 🔥 중요: 송금액이 0인 경우 Policy Compliance도 0이어야 함 (Cloud Functions용)
    if (totalRemittance === 0) {
      return {
        status: 'employed',
        compliance: 0,
        monthlyAverage: 0,
        totalRemittance: 0,
        monthsEmployed,
        companyName: activeEmployment.companyName || '',
        contractStartDate: activeEmployment.contractStartDate,
        contractEndDate: activeEmployment.contractEndDate,
        message: 'No remittances found - Compliance: 0%'
      };
    }
    
    // 7. 월 평균 송금액 계산
    const monthlyAverage = totalRemittance / monthsEmployed;
    
    // 8. Policy Compliance 계산
    const compliance = Math.min(100, (monthlyAverage / requiredMonthlyAmount) * 100);
    
    // 9. 결과 반환
    return {
      status: 'employed',
      compliance: Math.round(compliance),
      monthlyAverage: Math.round(monthlyAverage),
      totalRemittance: Math.round(totalRemittance),
      monthsEmployed,
      companyName: activeEmployment.companyName || '',
      contractStartDate: activeEmployment.contractStartDate,
      contractEndDate: activeEmployment.contractEndDate,
      message: `Compliance: ${Math.round(compliance)}% (${Math.round(monthlyAverage)} USD/month)`
    };
    
  } catch (error) {
    console.error('Policy Compliance 계산 오류 (Cloud Function):', error);
    return {
      status: 'error',
      compliance: 0,
      companyName: '',
      monthlyAverage: 0,
      totalRemittance: 0,
      monthsEmployed: 0,
      message: 'Calculation error occurred'
    };
  }
};

export default {
  calculatePolicyCompliance,
  calculatePolicyComplianceForCloudFunction
};
